import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ROOMS } from "@/data/rooms";
import { Reveal } from "@/components/site/Reveal";
import LazyImage from "@/components/site/LazyImage";

export function RoomCard({ room, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link to={`/stay/${room.slug}`} data-testid={`room-card-${room.slug}`} className="group block">
        <div className="relative overflow-hidden rounded-2xl">
          <LazyImage src={room.image} alt={room.name} className="aspect-[4/3]" imgClassName="transition-transform duration-[900ms] ease-out group-hover:scale-110" />
          <span className="absolute left-4 top-4 rounded-full bg-forest/85 px-3 py-1 text-xs font-medium uppercase tracking-wide text-champagne backdrop-blur-sm">
            {room.highlight}
          </span>
        </div>
        <div className="mt-5">
          <h3 className="font-display text-2xl font-bold text-forest md:text-3xl">{room.name}</h3>
          <p className="mt-1 text-charcoal/70">{room.tagline}</p>
          <p className="mt-3 text-sm text-brown">{room.capacity}</p>
          <span className="mt-4 inline-flex items-center gap-2 text-sm text-forest">
            <span className="border-b border-forest/30 pb-0.5 transition-colors group-hover:border-forest">View details</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function RoomsGrid() {
  return (
    <section data-testid="rooms-grid" className="bg-ivory py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal><p className="mb-5 text-xs uppercase tracking-widest-2 text-brown md:text-sm">Stay With Us</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-forest sm:text-5xl md:text-6xl">
                Rooms &amp; Cottages
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link to="/stay" data-testid="rooms-grid-all" className="group inline-flex items-center gap-2 text-forest">
              <span className="border-b border-forest/40 pb-1 group-hover:border-forest">View all stays</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {ROOMS.map((room, i) => (
            <RoomCard key={room.slug} room={room} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
